/**
 * SchoolExams — Full Exam & Report Card System
 * Question Bank (manual + AI), Vetting, Marking (auto/manual/AI), Rankings, Report Cards
 */
import React, { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogClose } from '@/components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Checkbox } from '@/components/ui/checkbox';
import ExamWindowSanityCheck from '@/components/school/ExamWindowSanityCheck';
import ExamNotificationsAudit from '@/components/school/ExamNotificationsAudit';
import ExamAccessDashboard from '@/components/school/ExamAccessDashboard';
import { toast } from 'sonner';
import { useAcademicContext } from '@/components/school/AcademicContext';
import {
  Plus, Loader2, ClipboardList, BookOpen, Trophy, FileText, Sparkles,
  CheckCircle, XCircle, Trash2, Edit2, Pencil, Search, AlertTriangle, AlertCircle,
  Brain, GraduationCap, ShieldCheck, Zap, ArrowLeft, Eye, ListChecks, Monitor, Send,
  Archive, ShieldAlert, DollarSign, UserMinus, RotateCcw, CheckSquare,
} from 'lucide-react';
import { AutoQuizTab, ProctoringTab, ExamDashboardTab, ParentDigestTab } from '@/components/school/ExamsPhaseBPanels';
import { OnlineExamLauncher } from '@/components/school/SchoolOnlineExam';
import ExamSubmissionsPanel from '@/components/school/ExamSubmissionsPanel';
import ResultsRosterPanel from '@/components/school/ResultsRosterPanel';
import { getToken } from '@/lib/authToken';
import { CampusHolidayBanner } from '@/components/school/CampusContext';
import WorkflowChecklist from '@/components/workflow/WorkflowChecklist';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const api = async (path, opts = {}) => {
  const res = await fetch(`${API_URL}/api/school${path}`, { ...opts, headers: { 'Authorization': `Bearer ${getToken()}`, 'Content-Type': 'application/json', ...opts.headers } });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    const d = err.detail;
    const msg = (d && typeof d === 'object') ? (d.message || d.reason || JSON.stringify(d)) : (d || 'Request failed');
    throw new Error(msg);
  }
  return res.json();
};

// `datetime-local` inputs use the user's local timezone in 'YYYY-MM-DDTHH:mm'
// format. Helpers below convert to/from the ISO-with-tz strings the API expects.
const toLocalInput = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};
const toISO = (local) => {
  if (!local) return null;
  const d = new Date(local);  // browser interprets as local time
  return Number.isNaN(d.getTime()) ? null : d.toISOString();
};



const Q_TYPES = [
  { value: 'true_false', label: 'True / False', color: 'bg-sky-100 text-sky-700' },
  { value: 'short_answer', label: 'Short Answer', color: 'bg-violet-100 text-violet-700' },
  { value: 'long_essay', label: 'Essay', color: 'bg-indigo-100 text-indigo-700' },
  { value: 'mcq_single', label: 'MCQ (Single)', color: 'bg-emerald-100 text-emerald-700' },
  { value: 'mcq_multi', label: 'MCQ (Multi)', color: 'bg-teal-100 text-teal-700' },
];
const Q_TYPE_MAP = Object.fromEntries(Q_TYPES.map(t => [t.value, t]));

const DIFFICULTIES = [
  { value: 'easy', label: 'Easy', color: 'bg-green-100 text-green-700' },
  { value: 'medium', label: 'Medium', color: 'bg-amber-100 text-amber-700' },
  { value: 'hard', label: 'Hard', color: 'bg-red-100 text-red-700' },
];
const DIFF_MAP = Object.fromEntries(DIFFICULTIES.map(d => [d.value, d]));

const Q_STATUS = {
  pending: { label: 'Pending', color: 'bg-amber-100 text-amber-700' },
  approved: { label: 'Approved', color: 'bg-green-100 text-green-700' },
  rejected: { label: 'Rejected', color: 'bg-red-100 text-red-700' },
};

const EXAM_STATUS = {
  draft: { label: 'Draft', color: 'bg-slate-100 text-slate-600' },
  active: { label: 'Active', color: 'bg-blue-100 text-blue-700' },
  completed: { label: 'Completed', color: 'bg-amber-100 text-amber-700' },
  published: { label: 'Published', color: 'bg-green-100 text-green-700' },
};

const VETTING_STATUS = {
  passed: { label: 'Passed', color: 'bg-green-100 text-green-700', icon: CheckCircle },
  warnings: { label: 'Warnings', color: 'bg-amber-100 text-amber-700', icon: AlertTriangle },
  failed: { label: 'Failed', color: 'bg-red-100 text-red-700', icon: AlertCircle },
};

const EXAM_TYPES = [
  { value: 'term', label: 'Term Exam' }, { value: 'mid-term', label: 'Mid-Term' },
  { value: 'quiz', label: 'Quiz' }, { value: 'assignment', label: 'Assignment' },
  { value: 'mock', label: 'Mock Exam' },
];

const MARKING_MODES = [
  { value: 'auto', label: 'Auto (Objective)', icon: Zap, desc: 'Auto-grade MCQ & True/False' },
  { value: 'manual', label: 'Manual', icon: Edit2, desc: 'Teacher grades all answers' },
  { value: 'ai', label: 'AI + Auto', icon: Brain, desc: 'Auto for objective, AI for essays' },
];

const StatCard = ({ label, value, color }) => (
  <div className={`rounded-lg px-4 py-2.5 ${color}`}>
    <div className="text-xs font-medium opacity-70">{label}</div>
    <div className="text-xl font-bold">{value}</div>
  </div>
);

// ============================= QUESTION FORM DIALOG =============================
const QuestionFormDialog = ({ open, onClose, tenantId, subjects, editing }) => {
  const qc = useQueryClient();
  const defaults = { question_type: 'mcq_single', subject_id: '', question_text: '', options: ['', '', '', ''], correct_answer: '', correct_answers: [], marks: 1, difficulty: 'medium', explanation: '', source: 'manual' };
  const [form, setForm] = useState(defaults);

  useEffect(() => {
    if (open) setForm(editing ? { ...defaults, ...editing, options: editing.options?.length ? editing.options : ['', '', '', ''], correct_answers: editing.correct_answers || [] } : defaults);
  }, [open, editing]);

  const mutation = useMutation({
    mutationFn: d => editing?.id
      ? api(`/tenants/${tenantId}/question-bank/${editing.id}`, { method: 'PUT', body: JSON.stringify(d) })
      : api(`/tenants/${tenantId}/question-bank`, { method: 'POST', body: JSON.stringify(d) }),
    onSuccess: () => { toast.success(editing ? 'Question updated' : 'Question created'); qc.invalidateQueries({ queryKey: ['question-bank'] }); onClose(); },
    onError: e => toast.error(e.message),
  });

  const handleSubmit = () => {
    if (!form.question_text.trim() || !form.subject_id) return toast.error('Fill required fields');
    const payload = { ...form, options: ['mcq_single', 'mcq_multi'].includes(form.question_type) ? form.options.filter(Boolean) : form.question_type === 'true_false' ? ['True', 'False'] : null };
    if (form.question_type === 'mcq_multi') { payload.correct_answer = null; }
    else { payload.correct_answers = null; }
    mutation.mutate(payload);
  };

  const setOpt = (i, val) => { const o = [...form.options]; o[i] = val; setForm({ ...form, options: o }); };
  const toggleMultiAnswer = (opt) => {
    const ca = form.correct_answers || [];
    setForm({ ...form, correct_answers: ca.includes(opt) ? ca.filter(a => a !== opt) : [...ca, opt] });
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
        <DialogHeader><DialogTitle>{editing ? 'Edit' : 'Create'} Question</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Subject *</Label>
              <Select value={form.subject_id} onValueChange={v => setForm({ ...form, subject_id: v })}>
                <SelectTrigger data-testid="q-subject-select"><SelectValue placeholder="Select" /></SelectTrigger>
                <SelectContent>{subjects.map(s => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Type *</Label>
              <Select value={form.question_type} onValueChange={v => setForm({ ...form, question_type: v, options: ['mcq_single', 'mcq_multi'].includes(v) ? ['', '', '', ''] : form.options, correct_answer: '', correct_answers: [] })}>
                <SelectTrigger data-testid="q-type-select"><SelectValue /></SelectTrigger>
                <SelectContent>{Q_TYPES.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <div>
            <Label className="text-xs">Question Text *</Label>
            <Textarea value={form.question_text} onChange={e => setForm({ ...form, question_text: e.target.value })} rows={3} placeholder="Enter question..." data-testid="q-text-input" />
          </div>

          {form.question_type === 'true_false' && (
            <div>
              <Label className="text-xs">Correct Answer</Label>
              <div className="flex gap-3 mt-1">
                {['True', 'False'].map(v => (
                  <button key={v} onClick={() => setForm({ ...form, correct_answer: v })}
                    className={`px-4 py-1.5 rounded-md text-sm font-medium border transition-colors ${form.correct_answer === v ? 'bg-emerald-100 border-emerald-400 text-emerald-700' : 'bg-white border-slate-200 text-slate-500'}`}
                  >{v}</button>
                ))}
              </div>
            </div>
          )}

          {['mcq_single', 'mcq_multi'].includes(form.question_type) && (
            <div className="space-y-2">
              <Label className="text-xs">Options {form.question_type === 'mcq_multi' ? '(check all correct)' : '(select correct)'}</Label>
              {form.options.map((opt, i) => (
                <div key={i} className="flex items-center gap-2">
                  {form.question_type === 'mcq_single' ? (
                    <button onClick={() => setForm({ ...form, correct_answer: opt })}
                      className={`w-5 h-5 rounded-full border-2 flex-shrink-0 transition-colors ${form.correct_answer === opt && opt ? 'border-emerald-500 bg-emerald-500' : 'border-slate-300'}`}
                    />
                  ) : (
                    <Checkbox checked={(form.correct_answers || []).includes(opt) && !!opt} onCheckedChange={() => opt && toggleMultiAnswer(opt)} />
                  )}
                  <Input value={opt} onChange={e => setOpt(i, e.target.value)} placeholder={`Option ${String.fromCharCode(65 + i)}`} className="h-8" />
                </div>
              ))}
              <Button variant="ghost" size="sm" onClick={() => setForm({ ...form, options: [...form.options, ''] })}>
                <Plus className="h-3 w-3 mr-1" /> Add Option
              </Button>
            </div>
          )}

          {form.question_type === 'short_answer' && (
            <div>
              <Label className="text-xs">Expected Answer</Label>
              <Input value={form.correct_answer} onChange={e => setForm({ ...form, correct_answer: e.target.value })} placeholder="Correct answer" />
            </div>
          )}

          {form.question_type === 'long_essay' && (
            <div>
              <Label className="text-xs">Model Answer (for AI grading reference)</Label>
              <Textarea value={form.correct_answer} onChange={e => setForm({ ...form, correct_answer: e.target.value })} rows={3} placeholder="Model answer outline..." />
            </div>
          )}

          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
            <div>
              <Label className="text-xs">Marks</Label>
              <Input type="number" min={0.5} step={0.5} value={form.marks} onChange={e => setForm({ ...form, marks: parseFloat(e.target.value) || 1 })} />
            </div>
            <div>
              <Label className="text-xs">Difficulty</Label>
              <Select value={form.difficulty} onValueChange={v => setForm({ ...form, difficulty: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{DIFFICULTIES.map(d => <SelectItem key={d.value} value={d.value}>{d.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Source</Label>
              <Input value={form.source} disabled className="bg-slate-50" />
            </div>
          </div>
          <div>
            <Label className="text-xs">Explanation (optional)</Label>
            <Textarea value={form.explanation} onChange={e => setForm({ ...form, explanation: e.target.value })} rows={2} placeholder="Why this is the correct answer..." />
          </div>
        </div>
        <DialogFooter className="mt-3">
          <DialogClose asChild><Button variant="outline">Cancel</Button></DialogClose>
          <Button onClick={handleSubmit} disabled={mutation.isPending} data-testid="save-question-btn">
            {mutation.isPending && <Loader2 className="h-4 w-4 animate-spin mr-1" />}
            {editing ? 'Update' : 'Create'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// ============================= AI GENERATE DIALOG =============================
const AIGenerateDialog = ({ open, onClose, tenantId, subjects }) => {
  const qc = useQueryClient();
  const [form, setForm] = useState({ subject_id: '', topic: '', question_types: ['mcq_single', 'true_false'], count: 5, difficulty: 'medium' });
  const toggleType = (t) => setForm(f => ({ ...f, question_types: f.question_types.includes(t) ? f.question_types.filter(x => x !== t) : [...f.question_types, t] }));

  const mutation = useMutation({
    mutationFn: d => api(`/tenants/${tenantId}/question-bank/ai-generate`, { method: 'POST', body: JSON.stringify(d) }),
    onSuccess: (r) => { toast.success(`${r.generated} questions generated! Review them in the Question Bank (Pending tab).`); qc.invalidateQueries({ queryKey: ['question-bank'] }); onClose(); },
    onError: e => toast.error(e.message),
  });

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Sparkles className="h-5 w-5 text-amber-500" /> AI Question Generator</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="text-xs">Subject *</Label>
            <Select value={form.subject_id} onValueChange={v => setForm({ ...form, subject_id: v })}>
              <SelectTrigger data-testid="ai-subject-select"><SelectValue placeholder="Select subject" /></SelectTrigger>
              <SelectContent>{subjects.map(s => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">Topic *</Label>
            <Input value={form.topic} onChange={e => setForm({ ...form, topic: e.target.value })} placeholder="e.g. Photosynthesis, Quadratic Equations..." data-testid="ai-topic-input" />
          </div>
          <div>
            <Label className="text-xs">Question Types</Label>
            <div className="flex flex-wrap gap-2 mt-1">
              {Q_TYPES.map(t => (
                <button key={t.value} onClick={() => toggleType(t.value)}
                  className={`px-3 py-1 rounded-full text-xs font-medium border transition-colors ${form.question_types.includes(t.value) ? t.color + ' border-current' : 'bg-white border-slate-200 text-slate-400'}`}
                >{t.label}</button>
              ))}
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Count</Label>
              <Input type="number" min={1} max={20} value={form.count} onChange={e => setForm({ ...form, count: parseInt(e.target.value) || 5 })} />
            </div>
            <div>
              <Label className="text-xs">Difficulty</Label>
              <Select value={form.difficulty} onValueChange={v => setForm({ ...form, difficulty: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{DIFFICULTIES.map(d => <SelectItem key={d.value} value={d.value}>{d.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
        </div>
        <DialogFooter className="mt-3">
          <DialogClose asChild><Button variant="outline">Cancel</Button></DialogClose>
          <Button onClick={() => mutation.mutate(form)} disabled={mutation.isPending || !form.subject_id || !form.topic} data-testid="ai-generate-btn">
            {mutation.isPending ? <><Loader2 className="h-4 w-4 animate-spin mr-1" /> Generating...</> : <><Sparkles className="h-4 w-4 mr-1" /> Generate</>}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// ============================= QUESTION BANK TAB =============================
const QuestionBankTab = ({ tenantId, subjects }) => {
  const qc = useQueryClient();
  const [filters, setFilters] = useState({ subject_id: '', question_type: '', difficulty: '', status: '', search: '' });
  const [showCreate, setShowCreate] = useState(false);
  const [showAI, setShowAI] = useState(false);
  const [editing, setEditing] = useState(null);
  const [page, setPage] = useState(1);

  const queryParams = new URLSearchParams();
  queryParams.set('page', page);
  queryParams.set('limit', '30');
  Object.entries(filters).forEach(([k, v]) => { if (v) queryParams.set(k, v); });

  const { data, isLoading } = useQuery({
    queryKey: ['question-bank', tenantId, filters, page],
    queryFn: () => api(`/tenants/${tenantId}/question-bank?${queryParams}`),
  });

  const approveMutation = useMutation({
    mutationFn: (id) => api(`/tenants/${tenantId}/question-bank/${id}/approve`, { method: 'PUT' }),
    onSuccess: () => { toast.success('Question approved'); qc.invalidateQueries({ queryKey: ['question-bank'] }); },
    onError: e => toast.error(e.message),
  });

  const rejectMutation = useMutation({
    mutationFn: (id) => api(`/tenants/${tenantId}/question-bank/${id}/reject`, { method: 'PUT' }),
    onSuccess: () => { toast.success('Question rejected'); qc.invalidateQueries({ queryKey: ['question-bank'] }); },
    onError: e => toast.error(e.message),
  });

  const deleteMutation = useMutation({
    mutationFn: (id) => api(`/tenants/${tenantId}/question-bank/${id}`, { method: 'DELETE' }),
    onSuccess: () => { toast.success('Question deleted'); qc.invalidateQueries({ queryKey: ['question-bank'] }); },
    onError: e => toast.error(e.message),
  });

  const questions = data?.questions || [];
  const total = data?.total || 0;
  const pendingCount = questions.filter(q => q.status === 'pending').length;

  return (
    <div className="space-y-4" data-testid="question-bank-tab">
      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <StatCard label="Total Questions" value={total} color="bg-slate-100 text-slate-700" />
        <StatCard label="Approved" value={questions.filter(q => q.status === 'approved').length} color="bg-green-50 text-green-700" />
        <StatCard label="Pending Review" value={pendingCount} color="bg-amber-50 text-amber-700" />
        <StatCard label="Rejected" value={questions.filter(q => q.status === 'rejected').length} color="bg-red-50 text-red-700" />
      </div>

      {/* Actions & Filters */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-wrap gap-2">
          <Select value={filters.subject_id} onValueChange={v => setFilters(f => ({ ...f, subject_id: v === 'all' ? '' : v }))}>
            <SelectTrigger className="w-full max-w-[150px] h-8 text-xs"><SelectValue placeholder="All Subjects" /></SelectTrigger>
            <SelectContent><SelectItem value="all">All Subjects</SelectItem>{subjects.map(s => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
          </Select>
          <Select value={filters.question_type} onValueChange={v => setFilters(f => ({ ...f, question_type: v === 'all' ? '' : v }))}>
            <SelectTrigger className="w-full max-w-[140px] h-8 text-xs"><SelectValue placeholder="All Types" /></SelectTrigger>
            <SelectContent><SelectItem value="all">All Types</SelectItem>{Q_TYPES.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}</SelectContent>
          </Select>
          <Select value={filters.status} onValueChange={v => setFilters(f => ({ ...f, status: v === 'all' ? '' : v }))}>
            <SelectTrigger className="w-full max-w-[130px] h-8 text-xs"><SelectValue placeholder="All Status" /></SelectTrigger>
            <SelectContent><SelectItem value="all">All Status</SelectItem><SelectItem value="pending">Pending</SelectItem><SelectItem value="approved">Approved</SelectItem><SelectItem value="rejected">Rejected</SelectItem></SelectContent>
          </Select>
          <div className="relative">
            <Search className="h-3.5 w-3.5 absolute left-2 top-2 text-slate-400" />
            <Input className="h-8 pl-7 w-full max-w-[180px] text-xs" placeholder="Search questions..." value={filters.search} onChange={e => setFilters(f => ({ ...f, search: e.target.value }))} data-testid="question-search" />
          </div>
        </div>
        <div className="flex gap-2">
          <Button size="sm" variant="outline" onClick={() => setShowAI(true)} data-testid="open-ai-generate-btn">
            <Sparkles className="h-4 w-4 mr-1 text-amber-500" /> AI Generate
          </Button>
          <Button size="sm" onClick={() => { setEditing(null); setShowCreate(true); }} data-testid="create-question-btn">
            <Plus className="h-4 w-4 mr-1" /> New Question
          </Button>
        </div>
      </div>

      {/* Questions Table */}
      <Card>
        {isLoading ? <div className="flex justify-center py-12"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div> : questions.length === 0 ? (
          <div className="text-center py-12 text-slate-400">
            <BookOpen className="h-10 w-10 mx-auto mb-2 opacity-40" />
            <p>No questions found. Create manually or generate with AI.</p>
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-[40%]">Question</TableHead>
                <TableHead>Type</TableHead>
                <TableHead>Subject</TableHead>
                <TableHead>Difficulty</TableHead>
                <TableHead>Marks</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {questions.map(q => {
                const typeInfo = Q_TYPE_MAP[q.question_type] || { label: q.question_type, color: 'bg-slate-100 text-slate-600' };
                const diffInfo = DIFF_MAP[q.difficulty] || { label: q.difficulty, color: 'bg-slate-100 text-slate-600' };
                const statusInfo = Q_STATUS[q.status] || { label: q.status, color: 'bg-slate-100 text-slate-600' };
                return (
                  <TableRow key={q.id} data-testid={`question-row-${q.id}`}>
                    <TableCell>
                      <div className="max-w-xs truncate text-sm font-medium">{q.question_text}</div>
                      {q.source === 'ai' && <Badge variant="outline" className="text-[10px] mt-0.5"><Sparkles className="h-2.5 w-2.5 mr-0.5" />AI</Badge>}
                    </TableCell>
                    <TableCell><Badge className={`text-[10px] ${typeInfo.color}`}>{typeInfo.label}</Badge></TableCell>
                    <TableCell className="text-xs text-slate-500">{q.subject_name}</TableCell>
                    <TableCell><Badge className={`text-[10px] ${diffInfo.color}`}>{diffInfo.label}</Badge></TableCell>
                    <TableCell className="text-sm">{q.marks}</TableCell>
                    <TableCell><Badge className={`text-[10px] ${statusInfo.color}`}>{statusInfo.label}</Badge></TableCell>
                    <TableCell>
                      <div className="flex justify-end gap-1">
                        {q.status === 'pending' && (
                          <>
                            <Button size="icon" variant="ghost" className="h-7 w-7 text-green-600 hover:text-green-700" onClick={() => approveMutation.mutate(q.id)} data-testid={`approve-q-${q.id}`} title="Approve"><CheckCircle className="h-4 w-4" /></Button>
                            <Button size="icon" variant="ghost" className="h-7 w-7 text-red-500 hover:text-red-600" onClick={() => rejectMutation.mutate(q.id)} data-testid={`reject-q-${q.id}`} title="Reject"><XCircle className="h-4 w-4" /></Button>
                          </>
                        )}
                        <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => { setEditing(q); setShowCreate(true); }} title="Edit"><Edit2 className="h-3.5 w-3.5" /></Button>
                        <Button size="icon" variant="ghost" className="h-7 w-7 text-red-400 hover:text-red-600" onClick={() => { if (window.confirm('Delete this question?')) deleteMutation.mutate(q.id); }} title="Delete"><Trash2 className="h-3.5 w-3.5" /></Button>
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        )}
        {total > 30 && (
          <div className="flex items-center justify-center gap-2 py-3 border-t">
            <Button size="sm" variant="outline" disabled={page <= 1} onClick={() => setPage(p => p - 1)}>Prev</Button>
            <span className="text-xs text-slate-500">Page {page} of {Math.ceil(total / 30)}</span>
            <Button size="sm" variant="outline" disabled={page >= Math.ceil(total / 30)} onClick={() => setPage(p => p + 1)}>Next</Button>
          </div>
        )}
      </Card>

      <QuestionFormDialog open={showCreate} onClose={() => setShowCreate(false)} tenantId={tenantId} subjects={subjects} editing={editing} />
      <AIGenerateDialog open={showAI} onClose={() => setShowAI(false)} tenantId={tenantId} subjects={subjects} />
    </div>
  );
};

// ============================= ASSIGN QUESTIONS DIALOG =============================
const AssignQuestionsDialog = ({ open, onClose, tenantId, examId, subjectId, subjects }) => {
  const qc = useQueryClient();
  const [selected, setSelected] = useState([]);
  const subjectName = subjects.find(s => s.id === subjectId)?.name || '';

  const { data, isLoading } = useQuery({
    queryKey: ['approved-questions', tenantId, subjectId],
    queryFn: () => api(`/tenants/${tenantId}/question-bank?subject_id=${subjectId}&status=approved&limit=100`),
    enabled: open && !!subjectId,
  });

  const mutation = useMutation({
    mutationFn: (ids) => api(`/tenants/${tenantId}/exams/${examId}/subjects/${subjectId}/questions`, { method: 'POST', body: JSON.stringify({ question_ids: ids }) }),
    onSuccess: (r) => { toast.success(`${r.added} questions assigned`); qc.invalidateQueries({ queryKey: ['exam-questions'] }); qc.invalidateQueries({ queryKey: ['school-exam-detail'] }); setSelected([]); onClose(); },
    onError: e => toast.error(e.message),
  });

  const questions = data?.questions || [];
  const toggle = (id) => setSelected(s => s.includes(id) ? s.filter(x => x !== id) : [...s, id]);

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-lg max-h-[80vh]">
        <DialogHeader><DialogTitle>Assign Questions — {subjectName}</DialogTitle></DialogHeader>
        <p className="text-xs text-slate-500">Select approved questions from the bank to add to this exam.</p>
        <ScrollArea className="max-h-[50vh] mt-2">
          {isLoading ? <Loader2 className="h-5 w-5 animate-spin mx-auto my-8" /> : questions.length === 0 ? (
            <p className="text-sm text-slate-400 text-center py-8">No approved questions for {subjectName}. Create or approve questions in the Question Bank first.</p>
          ) : (
            <div className="space-y-2">
              {questions.map(q => (
                <div key={q.id} onClick={() => toggle(q.id)} className={`flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${selected.includes(q.id) ? 'bg-indigo-50 border-indigo-300' : 'hover:bg-slate-50 border-slate-200'}`} data-testid={`assign-q-${q.id}`}>
                  <Checkbox checked={selected.includes(q.id)} className="mt-0.5" />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium truncate">{q.question_text}</div>
                    <div className="flex gap-2 mt-1">
                      <Badge className={`text-[10px] ${Q_TYPE_MAP[q.question_type]?.color || ''}`}>{Q_TYPE_MAP[q.question_type]?.label || q.question_type}</Badge>
                      <Badge className={`text-[10px] ${DIFF_MAP[q.difficulty]?.color || ''}`}>{q.difficulty}</Badge>
                      <span className="text-[10px] text-slate-400">{q.marks} marks</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </ScrollArea>
        <DialogFooter className="mt-3">
          <DialogClose asChild><Button variant="outline">Cancel</Button></DialogClose>
          <Button onClick={() => mutation.mutate(selected)} disabled={!selected.length || mutation.isPending} data-testid="assign-questions-btn">
            {mutation.isPending && <Loader2 className="h-4 w-4 animate-spin mr-1" />}
            Assign {selected.length} Question{selected.length !== 1 ? 's' : ''}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// ============================= VETTING PANEL =============================
const VettingPanel = ({ tenantId, exam }) => {
  const qc = useQueryClient();
  const [result, setResult] = useState(null);

  const vetMutation = useMutation({
    mutationFn: () => api(`/tenants/${tenantId}/exams/${exam.id}/vet`, { method: 'POST' }),
    onSuccess: (r) => { setResult(r); qc.invalidateQueries({ queryKey: ['school-exam-detail'] }); qc.invalidateQueries({ queryKey: ['school-exams'] }); toast.success(`Vetting complete: ${r.status}`); },
    onError: e => toast.error(e.message),
  });

  const statusInfo = exam.vetting_status ? VETTING_STATUS[exam.vetting_status] : null;
  const StatusIcon = statusInfo?.icon || ShieldCheck;

  return (
    <div className="space-y-4" data-testid="vetting-panel">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-sm font-semibold">Exam Vetting</h3>
          <p className="text-xs text-slate-500">Run automated checks to find errors before publishing.</p>
        </div>
        <Button onClick={() => vetMutation.mutate()} disabled={vetMutation.isPending} data-testid="run-vetting-btn">
          {vetMutation.isPending ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <ShieldCheck className="h-4 w-4 mr-1" />}
          Run Vetting
        </Button>
      </div>

      {statusInfo && !result && (
        <Card>
          <CardContent className="p-4 flex items-center gap-3">
            <StatusIcon className={`h-5 w-5 ${exam.vetting_status === 'passed' ? 'text-green-600' : exam.vetting_status === 'failed' ? 'text-red-600' : 'text-amber-600'}`} />
            <div>
              <Badge className={statusInfo.color}>{statusInfo.label}</Badge>
              <p className="text-xs text-slate-500 mt-1">Last vetted: {exam.vetted_at ? new Date(exam.vetted_at).toLocaleString() : 'unknown'}</p>
            </div>
          </CardContent>
        </Card>
      )}

      {(result || exam.vetting_issues?.length > 0) && (
        <div className="space-y-2">
          {(result?.issues || exam.vetting_issues?.filter(i => i.type === 'error') || []).map((issue, idx) => (
            <div key={`err-${idx}`} className="flex items-start gap-2 p-3 rounded-lg bg-red-50 border border-red-200">
              <AlertCircle className="h-4 w-4 text-red-500 mt-0.5 flex-shrink-0" />
              <div>
                <span className="text-xs font-medium text-red-700">{issue.field}</span>
                <p className="text-sm text-red-600">{issue.message}</p>
              </div>
            </div>
          ))}
          {(result?.warnings || exam.vetting_issues?.filter(i => i.type === 'warning') || []).map((warn, idx) => (
            <div key={`warn-${idx}`} className="flex items-start gap-2 p-3 rounded-lg bg-amber-50 border border-amber-200">
              <AlertTriangle className="h-4 w-4 text-amber-500 mt-0.5 flex-shrink-0" />
              <div>
                <span className="text-xs font-medium text-amber-700">{warn.field}</span>
                <p className="text-sm text-amber-600">{warn.message}</p>
              </div>
            </div>
          ))}
          {result?.status === 'passed' && (
            <div className="flex items-center gap-2 p-3 rounded-lg bg-green-50 border border-green-200">
              <CheckCircle className="h-4 w-4 text-green-600" />
              <p className="text-sm text-green-700 font-medium">All checks passed. Exam is ready to publish.</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// ============================= MARK ENTRY PANEL =============================
const MarkEntryPanel = ({ tenantId, exam, classes, subjects }) => {
  const qc = useQueryClient();
  const [selectedClass, setSelectedClass] = useState('');
  const [selectedSubject, setSelectedSubject] = useState('');
  const [marks, setMarks] = useState({});

  const { data: classStudents = [], isLoading: loadingStudents } = useQuery({
    queryKey: ['class-students', tenantId, selectedClass],
    queryFn: () => api(`/tenants/${tenantId}/classes/${selectedClass}/students`),
    enabled: !!selectedClass,
  });

  const { data: existingMarks = [] } = useQuery({
    queryKey: ['exam-marks', tenantId, exam.id, selectedSubject, selectedClass],
    queryFn: () => api(`/tenants/${tenantId}/marks?exam_id=${exam.id}&subject_id=${selectedSubject}&class_id=${selectedClass}`),
    enabled: !!selectedClass && !!selectedSubject,
  });

  useEffect(() => {
    if (existingMarks.length) {
      const m = {};
      existingMarks.forEach(em => { m[em.student_id] = { marks: em.marks, grade: em.grade, percentage: em.percentage }; });
      setMarks(m);
    }
  }, [existingMarks]);

  const saveMutation = useMutation({
    mutationFn: (data) => api(`/tenants/${tenantId}/marks`, { method: 'POST', body: JSON.stringify(data) }),
    onSuccess: (d) => { toast.success(`${d.entered} marks saved`); qc.invalidateQueries({ queryKey: ['exam-marks'] }); },
    onError: e => toast.error(e.message),
  });

  const autoMarkMutation = useMutation({
    mutationFn: () => api(`/tenants/${tenantId}/exams/${exam.id}/auto-mark?class_id=${selectedClass}&subject_id=${selectedSubject}`, { method: 'POST' }),
    onSuccess: (d) => { toast.success(`Auto-marked ${d.auto_marked} students (${d.marking_mode} mode)`); qc.invalidateQueries({ queryKey: ['exam-marks'] }); },
    onError: e => toast.error(e.message),
  });

  const examSubjects = (exam.subjects || []).map(es => subjects.find(s => s.id === es.subject_id)).filter(Boolean);
  const examClasses = (exam.class_ids || []).map(cid => classes.find(c => c.id === cid)).filter(Boolean);
  const markMode = MARKING_MODES.find(m => m.value === exam.marking_mode);

  const handleSave = () => {
    const entries = Object.entries(marks).map(([sid, m]) => ({ student_id: sid, marks: parseFloat(typeof m === 'object' ? m.marks : m) || 0 }));
    saveMutation.mutate({ exam_id: exam.id, subject_id: selectedSubject, class_id: selectedClass, entries });
  };

  return (
    <div className="space-y-4" data-testid="marks-entry-panel">
      <div className="flex flex-wrap items-end gap-3">
        <div className="min-w-0 sm:min-w-[160px]">
          <Label className="text-xs">Class</Label>
          <Select value={selectedClass} onValueChange={v => { setSelectedClass(v); setMarks({}); }}>
            <SelectTrigger><SelectValue placeholder="Select class" /></SelectTrigger>
            <SelectContent>{examClasses.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div className="min-w-0 sm:min-w-[160px]">
          <Label className="text-xs">Subject</Label>
          <Select value={selectedSubject} onValueChange={v => { setSelectedSubject(v); setMarks({}); }}>
            <SelectTrigger><SelectValue placeholder="Select subject" /></SelectTrigger>
            <SelectContent>{examSubjects.map(s => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        {selectedClass && selectedSubject && markMode && (
          <div className="flex items-center gap-2 ml-auto">
            <Badge variant="outline" className="text-xs"><markMode.icon className="h-3 w-3 mr-1 inline" />{markMode.label}</Badge>
            {(exam.marking_mode === 'auto' || exam.marking_mode === 'ai') && (
              <Button size="sm" variant="outline" onClick={() => autoMarkMutation.mutate()} disabled={autoMarkMutation.isPending} data-testid="auto-mark-btn">
                {autoMarkMutation.isPending ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Zap className="h-4 w-4 mr-1" />}
                {exam.marking_mode === 'ai' ? 'AI Mark' : 'Auto Mark'}
              </Button>
            )}
          </div>
        )}
      </div>

      {selectedClass && selectedSubject && (
        <>
          {loadingStudents ? <Loader2 className="h-5 w-5 animate-spin mx-auto" /> : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-[40px]">#</TableHead>
                  <TableHead>Student</TableHead>
                  <TableHead>Adm No.</TableHead>
                  <TableHead className="w-full max-w-[100px]">Marks</TableHead>
                  <TableHead className="w-[60px]">/ Max</TableHead>
                  <TableHead className="w-[60px]">Grade</TableHead>
                  <TableHead className="w-[60px]">%</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {classStudents.map((student, idx) => {
                  const m = marks[student.id];
                  const val = typeof m === 'object' ? m.marks : (m ?? '');
                  const grade = typeof m === 'object' ? m.grade : '';
                  const pct = typeof m === 'object' ? m.percentage : '';
                  return (
                    <TableRow key={student.id}>
                      <TableCell className="text-slate-400">{idx + 1}</TableCell>
                      <TableCell className="font-medium text-sm">{student.first_name} {student.last_name}</TableCell>
                      <TableCell className="text-xs text-slate-500">{student.admission_number}</TableCell>
                      <TableCell>
                        <Input type="number" min={0} max={exam.max_marks} value={val}
                          onChange={e => setMarks(prev => ({ ...prev, [student.id]: e.target.value }))}
                          className="h-8 w-20" data-testid={`marks-input-${student.id}`} />
                      </TableCell>
                      <TableCell className="text-xs text-slate-400">/ {exam.max_marks}</TableCell>
                      <TableCell>{grade && <Badge variant="outline" className="text-[10px]">{grade}</Badge>}</TableCell>
                      <TableCell className="text-xs text-slate-500">{pct ? `${pct}%` : ''}</TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
          <div className="flex justify-end">
            <Button onClick={handleSave} disabled={saveMutation.isPending} data-testid="save-marks-btn">
              {saveMutation.isPending && <Loader2 className="h-4 w-4 animate-spin mr-1" />}
              Save Marks
            </Button>
          </div>
        </>
      )}
    </div>
  );
};

// ============================= RANKINGS PANEL =============================
const RankingsPanel = ({ tenantId, examId, classes }) => {
  const [selectedClass, setSelectedClass] = useState('');
  const { data: rankings, isLoading } = useQuery({
    queryKey: ['exam-rankings', tenantId, examId, selectedClass],
    queryFn: () => api(`/tenants/${tenantId}/exams/${examId}/rankings/${selectedClass}`),
    enabled: !!selectedClass,
  });

  const medalColors = { 1: 'bg-yellow-100 text-yellow-800 border-yellow-300', 2: 'bg-slate-100 text-slate-700 border-slate-300', 3: 'bg-amber-100 text-amber-700 border-amber-300' };

  return (
    <div className="space-y-4" data-testid="rankings-panel">
      <div className="max-w-xs">
        <Label className="text-xs">Class</Label>
        <Select value={selectedClass} onValueChange={setSelectedClass}>
          <SelectTrigger data-testid="rankings-class-select"><SelectValue placeholder="Select class" /></SelectTrigger>
          <SelectContent>{classes.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
        </Select>
      </div>
      {isLoading ? <Loader2 className="h-5 w-5 animate-spin mx-auto" /> : rankings?.rankings?.length ? (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-[60px]">Pos</TableHead>
              <TableHead>Student</TableHead>
              <TableHead>Adm No.</TableHead>
              <TableHead>Total</TableHead>
              <TableHead>%</TableHead>
              <TableHead>GPA</TableHead>
              <TableHead>Grade</TableHead>
              <TableHead>Subjects</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rankings.rankings.map(r => (
              <TableRow key={r.student_id} data-testid={`ranking-row-${r.student_id}`}>
                <TableCell>
                  {r.position <= 3 ? (
                    <Badge className={`${medalColors[r.position]} border text-xs`}>#{r.position}</Badge>
                  ) : <span className="text-slate-500 text-sm">#{r.position}</span>}
                </TableCell>
                <TableCell className="font-medium text-sm">{r.student_name}</TableCell>
                <TableCell className="text-xs text-slate-500">{r.admission_number}</TableCell>
                <TableCell className="text-sm">{r.total_marks}/{r.total_max}</TableCell>
                <TableCell className="font-semibold text-sm">{r.percentage}%</TableCell>
                <TableCell className="text-sm">{r.average_gpa}</TableCell>
                <TableCell><Badge variant="outline" className="text-xs">{r.grade}</Badge></TableCell>
                <TableCell className="text-xs text-slate-500">{r.subjects_taken}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      ) : selectedClass ? (
        <div className="text-center py-10 text-slate-400">
          <Trophy className="h-10 w-10 mx-auto mb-2 opacity-40" />
          <p className="text-sm">No marks entered yet for this class</p>
        </div>
      ) : null}
    </div>
  );
};

// ============================= REPORT CARD PANEL =============================
const ReportCardPanel = ({ tenantId, examId, classes }) => {
  const [selectedClass, setSelectedClass] = useState('');
  const [selectedStudent, setSelectedStudent] = useState('');

  const { data: classStudents = [] } = useQuery({
    queryKey: ['class-students', tenantId, selectedClass],
    queryFn: () => api(`/tenants/${tenantId}/classes/${selectedClass}/students`),
    enabled: !!selectedClass,
  });

  const { data: report, isLoading } = useQuery({
    queryKey: ['report-card', tenantId, examId, selectedStudent],
    queryFn: () => api(`/tenants/${tenantId}/exams/${examId}/report-card/${selectedStudent}`),
    enabled: !!selectedStudent,
  });

  return (
    <div className="space-y-4" data-testid="report-card-panel">
      <div className="flex flex-wrap gap-3">
        <div className="min-w-0 sm:min-w-[160px]">
          <Label className="text-xs">Class</Label>
          <Select value={selectedClass} onValueChange={v => { setSelectedClass(v); setSelectedStudent(''); }}>
            <SelectTrigger><SelectValue placeholder="Select class" /></SelectTrigger>
            <SelectContent>{classes.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        {selectedClass && (
          <div className="min-w-0 sm:min-w-[200px]">
            <Label className="text-xs">Student</Label>
            <Select value={selectedStudent} onValueChange={setSelectedStudent}>
              <SelectTrigger data-testid="report-student-select"><SelectValue placeholder="Select student" /></SelectTrigger>
              <SelectContent>{classStudents.map(s => <SelectItem key={s.id} value={s.id}>{s.first_name} {s.last_name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
        )}
      </div>

      {isLoading ? <Loader2 className="h-5 w-5 animate-spin mx-auto" /> : report ? (
        <Card className="border-2">
          <CardContent className="p-6">
            {/* Header */}
            <div className="text-center border-b pb-4 mb-4">
              <h3 className="text-lg font-bold text-slate-900">Student Report Card</h3>
              <p className="text-sm text-slate-600">{report.exam?.name} — {report.exam?.type}</p>
            </div>
            {/* Student Info */}
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 text-sm mb-4 pb-4 border-b">
              <div><span className="text-slate-500">Name:</span> <span className="font-medium">{report.student?.name}</span></div>
              <div><span className="text-slate-500">Adm No:</span> <span className="font-medium">{report.student?.admission_number}</span></div>
              <div><span className="text-slate-500">Class:</span> <span className="font-medium">{report.class?.name}</span></div>
            </div>
            {/* Subjects */}
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Subject</TableHead>
                  <TableHead>Code</TableHead>
                  <TableHead>Marks</TableHead>
                  <TableHead>Max</TableHead>
                  <TableHead>%</TableHead>
                  <TableHead>Grade</TableHead>
                  <TableHead>GPA</TableHead>
                  <TableHead>Remarks</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {report.subjects?.map((subj, i) => (
                  <TableRow key={i}>
                    <TableCell className="font-medium text-sm">{subj.subject_name}</TableCell>
                    <TableCell className="text-xs text-slate-500">{subj.subject_code}</TableCell>
                    <TableCell className="text-sm">{subj.marks}</TableCell>
                    <TableCell className="text-xs text-slate-400">{subj.max_marks}</TableCell>
                    <TableCell className="font-medium text-sm">{subj.percentage}%</TableCell>
                    <TableCell><Badge variant="outline" className="text-xs">{subj.grade}</Badge></TableCell>
                    <TableCell className="text-sm">{subj.gpa}</TableCell>
                    <TableCell className="text-xs text-slate-500">{subj.remarks || '-'}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            {/* Summary */}
            <div className="mt-4 pt-4 border-t grid grid-cols-2 sm:grid-cols-4 gap-3">
              <StatCard label="Total" value={`${report.summary?.total_marks}/${report.summary?.total_max}`} color="bg-slate-100 text-slate-700" />
              <StatCard label="Percentage" value={`${report.summary?.percentage}%`} color="bg-blue-50 text-blue-700" />
              <StatCard label="Grade" value={report.summary?.grade} color="bg-indigo-50 text-indigo-700" />
              <StatCard label="Position" value={report.summary?.position ? `${report.summary.position} / ${report.summary.class_size}` : '-'} color="bg-amber-50 text-amber-700" />
            </div>
          </CardContent>
        </Card>
      ) : selectedStudent ? (
        <div className="text-center py-10 text-slate-400">
          <FileText className="h-10 w-10 mx-auto mb-2 opacity-40" />
          <p className="text-sm">No marks found for this student</p>
        </div>
      ) : null}
    </div>
  );
};

// ============================= EXAM DETAIL VIEW =============================
const ExamDetail = ({ tenantId, exam, onBack, classes, subjects }) => {
  const qc = useQueryClient();
  const [assignSubj, setAssignSubj] = useState(null);
  const [showAddSubject, setShowAddSubject] = useState(false);
  const [editingSubject, setEditingSubject] = useState(null);  // row being edited
  const [subjectForm, setSubjectForm] = useState({
    subject_id: '', max_marks: 100, pass_mark: '',
    available_from: '', available_until: '', grace_minutes: 0,
    duration_minutes: '',
  });
  const [qSubjectFilter, setQSubjectFilter] = useState('');
  const [onlineSubjectId, setOnlineSubjectId] = useState(null);

  const { data: detail } = useQuery({
    queryKey: ['school-exam-detail', tenantId, exam.id],
    queryFn: () => api(`/tenants/${tenantId}/exams/${exam.id}`),
  });

  const { data: examQuestions = [], isLoading: loadingQ } = useQuery({
    queryKey: ['exam-questions', tenantId, exam.id, qSubjectFilter],
    queryFn: () => api(`/tenants/${tenantId}/exams/${exam.id}/subjects/${qSubjectFilter}/questions`),
    enabled: !!qSubjectFilter,
  });

  const addSubjectMutation = useMutation({
    mutationFn: (d) => api(`/tenants/${tenantId}/exam-subjects`, { method: 'POST', body: JSON.stringify(d) }),
    onSuccess: () => { toast.success('Subject added'); setShowAddSubject(false); qc.invalidateQueries({ queryKey: ['school-exam-detail'] }); qc.invalidateQueries({ queryKey: ['school-exams'] }); },
    onError: e => toast.error(e.message),
  });

  const updateSubjectMutation = useMutation({
    mutationFn: ({ id, ...patch }) => api(`/tenants/${tenantId}/exam-subjects/${id}`, { method: 'PUT', body: JSON.stringify(patch) }),
    onSuccess: () => { toast.success('Subject updated'); setEditingSubject(null); qc.invalidateQueries({ queryKey: ['school-exam-detail'] }); },
    onError: e => toast.error(e.message),
  });

  const removeSubjectMutation = useMutation({
    mutationFn: (id) => api(`/tenants/${tenantId}/exam-subjects/${id}`, { method: 'DELETE' }),
    onSuccess: () => { toast.success('Subject removed'); qc.invalidateQueries({ queryKey: ['school-exam-detail'] }); },
    onError: e => toast.error(e.message),
  });

  const removeQuestionMutation = useMutation({
    mutationFn: (eqId) => api(`/tenants/${tenantId}/exams/${exam.id}/questions/${eqId}`, { method: 'DELETE' }),
    onSuccess: () => { toast.success('Question removed'); qc.invalidateQueries({ queryKey: ['exam-questions'] }); },
    onError: e => toast.error(e.message),
  });

  const statusMutation = useMutation({
    mutationFn: (status) => api(`/tenants/${tenantId}/exams/${exam.id}/status?status=${status}`, { method: 'PUT' }),
    onSuccess: () => { toast.success('Status updated'); qc.invalidateQueries({ queryKey: ['school-exam-detail'] }); qc.invalidateQueries({ queryKey: ['school-exams'] }); },
    onError: e => toast.error(e.message),
  });

  const archiveMutation = useMutation({
    mutationFn: () => api(`/tenants/${tenantId}/exams/${exam.id}/archive`, { method: 'PUT' }),
    onSuccess: () => { toast.success('Exam archived'); qc.invalidateQueries({ queryKey: ['school-exam-detail'] }); qc.invalidateQueries({ queryKey: ['school-exams'] }); },
    onError: e => toast.error(e.message),
  });
  const unarchiveMutation = useMutation({
    mutationFn: () => api(`/tenants/${tenantId}/exams/${exam.id}/unarchive`, { method: 'PUT' }),
    onSuccess: () => { toast.success('Exam restored'); qc.invalidateQueries({ queryKey: ['school-exam-detail'] }); qc.invalidateQueries({ queryKey: ['school-exams'] }); },
    onError: e => toast.error(e.message),
  });
  const [showDelete, setShowDelete] = useState(false);
  const [confirmDeleteName, setConfirmDeleteName] = useState('');
  const deleteMutation = useMutation({
    mutationFn: () => api(`/tenants/${tenantId}/exams/${exam.id}?confirm_name=${encodeURIComponent(confirmDeleteName)}`, { method: 'DELETE' }),
    onSuccess: (r) => {
      const c = r.deleted || {};
      toast.success(`Deleted ${r.exam_name} · ${(c.submissions || 0)} submissions, ${(c.marks || 0)} marks removed`);
      setShowDelete(false); setConfirmDeleteName('');
      qc.invalidateQueries({ queryKey: ['school-exams'] });
      onBack?.();
    },
    onError: e => toast.error(e.message),
  });

  const d = detail || exam;
  const detailClasses = (d.class_ids || []).map(cid => classes.find(c => c.id === cid)).filter(Boolean);
  const detailSubjects = d.subjects || [];
  const examStatusInfo = EXAM_STATUS[d.status] || EXAM_STATUS.draft;
  const markMode = MARKING_MODES.find(m => m.value === d.marking_mode) || MARKING_MODES[0];

  return (
    <div className="space-y-5" data-testid="exam-detail-panel">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <button onClick={onBack} className="text-xs text-indigo-600 hover:underline flex items-center gap-1 mb-1" data-testid="back-to-exams"><ArrowLeft className="h-3 w-3" /> Back to Exams</button>
          <h2 className="text-lg font-bold text-slate-900">{d.name}</h2>
          <div className="flex items-center gap-2 mt-1">
            <Badge className={examStatusInfo.color}>{examStatusInfo.label}</Badge>
            <Badge variant="outline" className="capitalize text-xs">{d.exam_type}</Badge>
            <Badge variant="outline" className="text-xs"><markMode.icon className="h-3 w-3 mr-1 inline" />{markMode.label}</Badge>
            {d.vetting_status && (
              <Badge className={VETTING_STATUS[d.vetting_status]?.color || ''}>{d.vetting_status}</Badge>
            )}
          </div>
        </div>
        <div className="flex gap-2 flex-wrap">
          {d.status === 'draft' && <Button size="sm" variant="outline" onClick={() => statusMutation.mutate('active')} data-testid="activate-exam-btn">Activate</Button>}
          {d.status === 'active' && <Button size="sm" variant="outline" onClick={() => statusMutation.mutate('completed')}>Complete</Button>}
          {d.status === 'completed' && <Button size="sm" onClick={() => statusMutation.mutate('published')} data-testid="publish-exam-btn">Publish</Button>}
          {d.status === 'archived' ? (
            <Button size="sm" variant="outline" onClick={() => unarchiveMutation.mutate()} disabled={unarchiveMutation.isPending} data-testid="unarchive-exam-btn"><RotateCcw className="h-3 w-3 mr-1" /> Restore</Button>
          ) : (
            <Button size="sm" variant="outline" onClick={() => { if (window.confirm(`Archive "${d.name}"? Hidden from students, marks preserved.`)) archiveMutation.mutate(); }} disabled={archiveMutation.isPending} data-testid="archive-exam-btn"><Archive className="h-3 w-3 mr-1" /> Archive</Button>
          )}
          <Button size="sm" variant="outline" className="text-red-600 border-red-200 hover:bg-red-50" onClick={() => setShowDelete(true)} data-testid="delete-exam-btn"><Trash2 className="h-3 w-3 mr-1" /> Delete</Button>
        </div>
      </div>

      {/* iter-361 isolation banner — surfaces the subject this exam is bound
          to and reassures admins that actions don't bleed into other exams. */}
      <div
        data-testid="exam-isolation-banner"
        className={`rounded-lg border px-3 py-2 text-xs flex items-start gap-2 ${
          detailSubjects.length === 1
            ? 'bg-emerald-50 border-emerald-200 text-emerald-800'
            : detailSubjects.length === 0
              ? 'bg-amber-50 border-amber-200 text-amber-800'
              : 'bg-amber-50 border-amber-200 text-amber-800'
        }`}
      >
        <ShieldCheck className="h-4 w-4 shrink-0 mt-0.5" />
        <div className="leading-snug">
          {detailSubjects.length === 1 ? (
            <>
              <strong>Subject:</strong> {detailSubjects[0]?.name || '(unnamed)'}
              {detailSubjects[0]?.code && <span className="ml-1 opacity-70">· {detailSubjects[0].code}</span>}
              <span className="mx-1">·</span>
              <span>This exam is independent — activation, attempts, marks, resets, and notifications stay isolated from every other exam.</span>
            </>
          ) : detailSubjects.length === 0 ? (
            <>No subject attached yet. Add a subject under the Setup tab — questions, marks, and student access all key off the subject.</>
          ) : (
            <>
              <strong>Legacy combined exam ·</strong> {detailSubjects.length} subjects:&nbsp;
              {detailSubjects.map(s => s.name).filter(Boolean).join(', ')}.
              New exams created in this iteration are single-subject — each subject is fully independent.
            </>
          )}
        </div>
      </div>

      <Tabs defaultValue="setup">
        <TabsList className="w-full justify-start overflow-x-auto">
          <TabsTrigger value="setup"><ListChecks className="h-4 w-4 mr-1" /> Setup</TabsTrigger>
          <TabsTrigger value="questions"><BookOpen className="h-4 w-4 mr-1" /> Questions</TabsTrigger>
          <TabsTrigger value="vetting"><ShieldCheck className="h-4 w-4 mr-1" /> Vetting</TabsTrigger>
          <TabsTrigger value="submissions" data-testid="exam-tab-submissions"><Send className="h-4 w-4 mr-1" /> Submissions</TabsTrigger>
          <TabsTrigger value="publishing" data-testid="exam-tab-publishing"><CheckSquare className="h-4 w-4 mr-1" /> Publishing</TabsTrigger>
          <TabsTrigger value="marks"><ClipboardList className="h-4 w-4 mr-1" /> Marks</TabsTrigger>
          <TabsTrigger value="rankings"><Trophy className="h-4 w-4 mr-1" /> Rankings</TabsTrigger>
          <TabsTrigger value="reports"><GraduationCap className="h-4 w-4 mr-1" /> Report Cards</TabsTrigger>
          <TabsTrigger value="waivers" data-testid="exam-tab-waivers"><DollarSign className="h-4 w-4 mr-1" /> Fee Waivers</TabsTrigger>
          <TabsTrigger value="online"><Monitor className="h-4 w-4 mr-1" /> Online</TabsTrigger>
        </TabsList>

        {/* SETUP TAB */}
        <TabsContent value="setup" className="mt-4 space-y-4">
          <ExamReadinessChecklist tenantId={tenantId} examId={exam.id} />
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-semibold">Exam Subjects</h3>
            <Button size="sm" onClick={() => setShowAddSubject(true)} data-testid="add-exam-subject-btn"><Plus className="h-4 w-4 mr-1" /> Add Subject</Button>
          </div>
          <Card>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Subject</TableHead>
                  <TableHead>Max</TableHead>
                  <TableHead>Pass</TableHead>
                  <TableHead>Opens</TableHead>
                  <TableHead>Closes</TableHead>
                  <TableHead>Duration</TableHead>
                  <TableHead>Qs</TableHead>
                  <TableHead className="w-[80px]"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {detailSubjects.length === 0 ? (
                  <TableRow><TableCell colSpan={8} className="text-center py-8 text-slate-400">No subjects added yet</TableCell></TableRow>
                ) : detailSubjects.map(es => {
                  const fmt = (v) => v ? new Date(v).toLocaleString() : '—';
                  return (
                    <TableRow key={es.id} data-testid={`exam-subject-row-${es.id}`}>
                      <TableCell className="font-medium text-sm">{es.subject_name}<div className="text-[10px] text-slate-400">{es.subject_code}</div></TableCell>
                      <TableCell>{es.max_marks}</TableCell>
                      <TableCell className="text-sm">{es.pass_mark || '-'}</TableCell>
                      <TableCell className="text-xs text-slate-500">{fmt(es.available_from)}</TableCell>
                      <TableCell className="text-xs text-slate-500">{fmt(es.available_until)}{es.grace_minutes ? <span className="text-[10px] text-amber-600"> +{es.grace_minutes}min</span> : null}</TableCell>
                      <TableCell className="text-xs text-slate-500">{es.duration_minutes ? `${es.duration_minutes} min` : '-'}</TableCell>
                      <TableCell><Badge variant="outline" className="text-xs">{es.question_count || 0}</Badge></TableCell>
                      <TableCell className="flex gap-1">
                        <Button size="icon" variant="ghost" className="h-7 w-7 text-indigo-500"
                                onClick={() => setEditingSubject({
                                  id: es.id, subject_name: es.subject_name,
                                  max_marks: es.max_marks ?? 100, pass_mark: es.pass_mark || '',
                                  available_from: es.available_from ? toLocalInput(es.available_from) : '',
                                  available_until: es.available_until ? toLocalInput(es.available_until) : '',
                                  grace_minutes: es.grace_minutes || 0,
                                  duration_minutes: es.duration_minutes || '',
                                })}
                                data-testid={`edit-exam-subject-${es.id}`}>
                          <Pencil className="h-3.5 w-3.5" />
                        </Button>
                        <Button size="icon" variant="ghost" className="h-7 w-7 text-red-400" onClick={() => { if (window.confirm('Remove this subject?')) removeSubjectMutation.mutate(es.id); }}><Trash2 className="h-3.5 w-3.5" /></Button>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </Card>
          {/* Info row */}
          <ExamWindowSanityCheck tenantId={tenantId} exam={d} />
          <div className="flex flex-wrap gap-4 text-xs text-slate-500">
            <span>Classes: {detailClasses.map(c => c.name).join(', ') || 'None'}</span>
            <span>Dates: {d.start_date || '?'} — {d.end_date || '?'}</span>
            <span>Max Marks: {d.max_marks}</span>
          </div>
          <ExamNotificationsAudit tenantId={tenantId} examId={d.id || d._id} />
          <ExamAccessDashboard tenantId={tenantId} examId={d.id || d._id} />
        </TabsContent>

        {/* QUESTIONS TAB */}
        <TabsContent value="questions" className="mt-4 space-y-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Label className="text-xs">Subject:</Label>
              <Select value={qSubjectFilter} onValueChange={setQSubjectFilter}>
                <SelectTrigger className="w-full max-w-[200px]" data-testid="exam-q-subject-filter"><SelectValue placeholder="Select subject" /></SelectTrigger>
                <SelectContent>{detailSubjects.map(es => <SelectItem key={es.subject_id} value={es.subject_id}>{es.subject_name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            {qSubjectFilter && (
              <Button size="sm" onClick={() => setAssignSubj(qSubjectFilter)} data-testid="open-assign-questions-btn">
                <Plus className="h-4 w-4 mr-1" /> Assign from Bank
              </Button>
            )}
          </div>
          {qSubjectFilter ? (
            <Card>
              {loadingQ ? <div className="flex justify-center py-8"><Loader2 className="h-5 w-5 animate-spin text-slate-400" /></div> : examQuestions.length === 0 ? (
                <div className="text-center py-10 text-slate-400">
                  <BookOpen className="h-8 w-8 mx-auto mb-2 opacity-40" />
                  <p className="text-sm">No questions assigned yet. Click "Assign from Bank" to add.</p>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-[40px]">#</TableHead>
                      <TableHead className="w-[45%]">Question</TableHead>
                      <TableHead>Type</TableHead>
                      <TableHead>Marks</TableHead>
                      <TableHead>Difficulty</TableHead>
                      <TableHead className="w-[40px]"></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {examQuestions.map((eq, idx) => (
                      <TableRow key={eq.id}>
                        <TableCell className="text-slate-400 text-sm">{idx + 1}</TableCell>
                        <TableCell>
                          <div className="text-sm font-medium max-w-xs truncate">{eq.question_text}</div>
                          {eq.options && <div className="text-[10px] text-slate-400 mt-0.5">{eq.options.slice(0, 4).join(' | ')}</div>}
                        </TableCell>
                        <TableCell><Badge className={`text-[10px] ${Q_TYPE_MAP[eq.question_type]?.color || ''}`}>{Q_TYPE_MAP[eq.question_type]?.label || eq.question_type}</Badge></TableCell>
                        <TableCell className="text-sm">{eq.marks}</TableCell>
                        <TableCell><Badge className={`text-[10px] ${DIFF_MAP[eq.difficulty]?.color || ''}`}>{eq.difficulty}</Badge></TableCell>
                        <TableCell>
                          <Button size="icon" variant="ghost" className="h-7 w-7 text-red-400" onClick={() => removeQuestionMutation.mutate(eq.id)}><Trash2 className="h-3.5 w-3.5" /></Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </Card>
          ) : (
            <div className="text-center py-10 text-slate-400">
              <p className="text-sm">Select a subject above to manage questions</p>
            </div>
          )}
        </TabsContent>

        {/* VETTING TAB */}
        <TabsContent value="vetting" className="mt-4">
          <Card><CardContent className="p-4"><VettingPanel tenantId={tenantId} exam={d} /></CardContent></Card>
        </TabsContent>

        {/* SUBMISSIONS & MARKING TAB — full post-submission workflow */}
        <TabsContent value="submissions" className="mt-4">
          <ExamSubmissionsPanel tenantId={tenantId} exam={d} classes={detailClasses} subjects={subjects} />
        </TabsContent>

        {/* PUBLISHING TAB (iter-408) — selective publish / withhold per student */}
        <TabsContent value="publishing" className="mt-4">
          <ResultsRosterPanel tenantId={tenantId} exam={d} subjects={subjects} />
        </TabsContent>

        {/* MARKS TAB */}
        <TabsContent value="marks" className="mt-4">
          <Card><CardContent className="p-4"><MarkEntryPanel tenantId={tenantId} exam={d} classes={detailClasses} subjects={subjects} /></CardContent></Card>
        </TabsContent>

        {/* RANKINGS TAB */}
        <TabsContent value="rankings" className="mt-4">
          <Card><CardContent className="p-4"><RankingsPanel tenantId={tenantId} examId={d.id} classes={detailClasses} /></CardContent></Card>
        </TabsContent>

        {/* REPORT CARDS TAB */}
        <TabsContent value="reports" className="mt-4">
          <Card><CardContent className="p-4"><ReportCardPanel tenantId={tenantId} examId={d.id} classes={detailClasses} /></CardContent></Card>
        </TabsContent>

        {/* FEE WAIVERS TAB */}
        <TabsContent value="waivers" className="mt-4">
          <ExamWaiversPanel tenantId={tenantId} examId={d.id} examName={d.name} />
        </TabsContent>

        {/* ONLINE EXAM TAB */}
        <TabsContent value="online" className="mt-4 space-y-4">
          {d.status !== 'active' ? (
            <Card className="border-dashed"><CardContent className="py-10 text-center text-slate-400"><Monitor className="h-8 w-8 mx-auto mb-2 opacity-50" />Exam must be Active to enable online testing</CardContent></Card>
          ) : onlineSubjectId ? (
            <OnlineExamLauncher
              tenantId={tenantId}
              examId={d.id}
              subjectId={onlineSubjectId}
              subjectName={subjects.find(s => s.id === onlineSubjectId)?.name || ''}
              examName={d.name}
              onClose={() => setOnlineSubjectId(null)}
            />
          ) : (
            <>
              <Card>
                <CardContent className="p-4 space-y-3">
                  <div className="flex items-center gap-2 mb-3">
                    <Monitor className="h-5 w-5 text-indigo-600" />
                    <div>
                      <h3 className="font-semibold text-sm">Online Exam Portal</h3>
                      <p className="text-xs text-slate-500">Launch a timed, online exam session for any subject</p>
                    </div>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {detailSubjects.map(es => {
                      const subj = subjects.find(s => s.id === es.subject_id);
                      return (
                        <Card key={es.subject_id} className="hover:shadow-md transition-shadow cursor-pointer border-slate-200 hover:border-indigo-300"
                          onClick={() => setOnlineSubjectId(es.subject_id)}
                          data-testid={`online-subject-${es.subject_id}`}
                        >
                          <CardContent className="p-3 flex items-center justify-between">
                            <div>
                              <p className="font-medium text-sm">{subj?.name || 'Unknown'}</p>
                              <p className="text-xs text-slate-500">{es.duration_minutes || 60} min | {es.max_marks} marks | {es.question_count || 0} questions</p>
                            </div>
                            <Badge className="bg-green-100 text-green-700">Take Exam</Badge>
                          </CardContent>
                        </Card>
                      );
                    })}
                  </div>
                  {detailSubjects.length === 0 && <p className="text-sm text-slate-400 text-center py-4">No subjects configured. Add subjects in Setup tab first.</p>}
                </CardContent>
              </Card>
            </>
          )}
        </TabsContent>
      </Tabs>

      {/* Add Subject Dialog */}
      <Dialog open={showAddSubject} onOpenChange={setShowAddSubject}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Add Subject Paper</DialogTitle></DialogHeader>
          <p className="text-xs text-slate-500 -mt-1">Each paper carries its own start &amp; end window. Students cannot start the paper before <em>Opens</em>; submissions are rejected after <em>Closes</em> (plus grace).</p>
          <div className="space-y-3">
            <div>
              <Label className="text-xs">Subject *</Label>
              <Select value={subjectForm.subject_id} onValueChange={v => setSubjectForm({ ...subjectForm, subject_id: v })}>
                <SelectTrigger data-testid="exam-subject-select"><SelectValue placeholder="Select" /></SelectTrigger>
                <SelectContent>{subjects.map(s => <SelectItem key={s.id} value={s.id}>{s.name} ({s.code})</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs">Max Marks</Label><Input type="number" value={subjectForm.max_marks} onChange={e => setSubjectForm({ ...subjectForm, max_marks: parseFloat(e.target.value) || 100 })} data-testid="exam-subject-max-marks" /></div>
              <div><Label className="text-xs">Pass Mark</Label><Input type="number" value={subjectForm.pass_mark} onChange={e => setSubjectForm({ ...subjectForm, pass_mark: e.target.value })} placeholder="optional" /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Opens *</Label>
                <Input type="datetime-local" value={subjectForm.available_from} onChange={e => setSubjectForm({ ...subjectForm, available_from: e.target.value })} data-testid="exam-subject-opens" />
              </div>
              <div>
                <Label className="text-xs">Closes *</Label>
                <Input type="datetime-local" value={subjectForm.available_until} onChange={e => setSubjectForm({ ...subjectForm, available_until: e.target.value })} data-testid="exam-subject-closes" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs">Duration (min)</Label><Input type="number" value={subjectForm.duration_minutes} onChange={e => setSubjectForm({ ...subjectForm, duration_minutes: parseInt(e.target.value) || '' })} placeholder="e.g. 120" /></div>
              <div><Label className="text-xs">Grace (min)</Label><Input type="number" value={subjectForm.grace_minutes} onChange={e => setSubjectForm({ ...subjectForm, grace_minutes: parseInt(e.target.value) || 0 })} placeholder="optional" /></div>
            </div>
          </div>
          <DialogFooter className="mt-3">
            <DialogClose asChild><Button variant="outline">Cancel</Button></DialogClose>
            <Button
              onClick={() => addSubjectMutation.mutate({
                exam_id: d.id,
                subject_id: subjectForm.subject_id,
                max_marks: subjectForm.max_marks,
                pass_mark: subjectForm.pass_mark ? parseFloat(subjectForm.pass_mark) : null,
                duration_minutes: subjectForm.duration_minutes || null,
                grace_minutes: subjectForm.grace_minutes || 0,
                available_from: toISO(subjectForm.available_from),
                available_until: toISO(subjectForm.available_until),
              })}
              disabled={addSubjectMutation.isPending || !subjectForm.subject_id || !subjectForm.available_from || !subjectForm.available_until}
              data-testid="submit-exam-subject-btn">
              {addSubjectMutation.isPending && <Loader2 className="h-4 w-4 animate-spin mr-1" />}
              Add Subject
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Subject Dialog — reuses the same datetime-local pickers so
          admins can correct an opens/closes timestamp on an existing paper. */}
      <Dialog open={!!editingSubject} onOpenChange={(o) => !o && setEditingSubject(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Edit Subject Paper</DialogTitle></DialogHeader>
          {editingSubject && (
            <>
              <p className="text-xs text-slate-500 -mt-1">Editing <strong>{editingSubject.subject_name}</strong>.</p>
              <div className="space-y-3">
                <div className="grid grid-cols-2 gap-3">
                  <div><Label className="text-xs">Max Marks</Label><Input type="number" value={editingSubject.max_marks} onChange={e => setEditingSubject({ ...editingSubject, max_marks: parseFloat(e.target.value) || 100 })} /></div>
                  <div><Label className="text-xs">Pass Mark</Label><Input type="number" value={editingSubject.pass_mark} onChange={e => setEditingSubject({ ...editingSubject, pass_mark: e.target.value })} /></div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs">Opens *</Label>
                    <Input type="datetime-local" value={editingSubject.available_from} onChange={e => setEditingSubject({ ...editingSubject, available_from: e.target.value })} data-testid="edit-exam-subject-opens" />
                  </div>
                  <div>
                    <Label className="text-xs">Closes *</Label>
                    <Input type="datetime-local" value={editingSubject.available_until} onChange={e => setEditingSubject({ ...editingSubject, available_until: e.target.value })} data-testid="edit-exam-subject-closes" />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div><Label className="text-xs">Duration (min)</Label><Input type="number" value={editingSubject.duration_minutes} onChange={e => setEditingSubject({ ...editingSubject, duration_minutes: parseInt(e.target.value) || '' })} /></div>
                  <div><Label className="text-xs">Grace (min)</Label><Input type="number" value={editingSubject.grace_minutes} onChange={e => setEditingSubject({ ...editingSubject, grace_minutes: parseInt(e.target.value) || 0 })} /></div>
                </div>
              </div>
              <DialogFooter className="mt-3">
                <DialogClose asChild><Button variant="outline">Cancel</Button></DialogClose>
                <Button
                  onClick={() => updateSubjectMutation.mutate({
                    id: editingSubject.id,
                    max_marks: editingSubject.max_marks,
                    pass_mark: editingSubject.pass_mark === '' ? null : parseFloat(editingSubject.pass_mark),
                    duration_minutes: editingSubject.duration_minutes || null,
                    grace_minutes: editingSubject.grace_minutes || 0,
                    available_from: toISO(editingSubject.available_from),
                    available_until: toISO(editingSubject.available_until),
                  })}
                  disabled={updateSubjectMutation.isPending}
                  data-testid="save-exam-subject-btn">
                  {updateSubjectMutation.isPending && <Loader2 className="h-4 w-4 animate-spin mr-1" />}
                  Save changes
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Assign Questions Dialog */}
      <AssignQuestionsDialog open={!!assignSubj} onClose={() => setAssignSubj(null)} tenantId={tenantId} examId={d.id} subjectId={assignSubj || ''} subjects={subjects} />

      {/* Permanent Delete Dialog — requires typing the exam name to confirm. */}
      <Dialog open={showDelete} onOpenChange={setShowDelete}>
        <DialogContent data-testid="delete-exam-dialog">
          <DialogHeader>
            <DialogTitle className="text-red-600 flex items-center gap-2"><ShieldAlert className="h-5 w-5" /> Delete exam permanently?</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-slate-700">This will permanently remove <strong>{d.name}</strong> and ALL related data — subjects, questions, submissions, sessions, marks and audit log. <strong className="text-red-600">This cannot be undone.</strong></p>
          <p className="text-xs text-slate-500">If you only want to hide the exam, use Archive instead.</p>
          <div className="mt-2">
            <Label className="text-xs">Type the exam name to confirm: <code className="bg-slate-100 px-1">{d.name}</code></Label>
            <Input value={confirmDeleteName} onChange={(e) => setConfirmDeleteName(e.target.value)} placeholder={d.name} data-testid="delete-confirm-input" autoFocus />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowDelete(false); setConfirmDeleteName(''); }}>Cancel</Button>
            <Button
              variant="destructive"
              onClick={() => deleteMutation.mutate()}
              disabled={deleteMutation.isPending || confirmDeleteName.trim().toLowerCase() !== (d.name || '').trim().toLowerCase()}
              data-testid="confirm-delete-exam-btn"
            >
              {deleteMutation.isPending && <Loader2 className="h-4 w-4 animate-spin mr-1" />}
              Delete permanently
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// =========================== EXAM FEE WAIVERS PANEL ===========================
const ExamWaiversPanel = ({ tenantId, examId, examName }) => {
  const qc = useQueryClient();
  const [reason, setReason] = useState('');
  const [studentId, setStudentId] = useState('');

  const { data, isLoading } = useQuery({
    queryKey: ['exam-waivers', examId],
    queryFn: () => api(`/tenants/${tenantId}/exams/${examId}/waivers`),
  });

  const grantBlanket = useMutation({
    mutationFn: () => api(`/tenants/${tenantId}/exams/${examId}/waivers`, { method: 'POST', body: JSON.stringify({ reason }) }),
    onSuccess: () => { toast.success('Blanket waiver granted — all students bypass fees for this exam'); setReason(''); qc.invalidateQueries({ queryKey: ['exam-waivers', examId] }); },
    onError: e => toast.error(e.message),
  });
  const revokeBlanket = useMutation({
    mutationFn: () => api(`/tenants/${tenantId}/exams/${examId}/waivers`, { method: 'DELETE' }),
    onSuccess: () => { toast.success('Blanket waiver revoked'); qc.invalidateQueries({ queryKey: ['exam-waivers', examId] }); },
    onError: e => toast.error(e.message),
  });
  const grantStudent = useMutation({
    mutationFn: () => api(`/tenants/${tenantId}/exams/${examId}/waivers`, { method: 'POST', body: JSON.stringify({ student_id: studentId, reason }) }),
    onSuccess: () => { toast.success('Waiver granted'); setStudentId(''); setReason(''); qc.invalidateQueries({ queryKey: ['exam-waivers', examId] }); },
    onError: e => toast.error(e.message),
  });
  const revokeStudent = useMutation({
    mutationFn: (wid) => api(`/tenants/${tenantId}/exams/${examId}/waivers/${wid}`, { method: 'DELETE' }),
    onSuccess: () => { toast.success('Waiver revoked'); qc.invalidateQueries({ queryKey: ['exam-waivers', examId] }); },
    onError: e => toast.error(e.message),
  });

  return (
    <div className="space-y-4" data-testid="exam-waivers-panel">
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base flex items-center gap-2"><DollarSign className="h-4 w-4" /> Fee waivers for {examName}</CardTitle>
          <p className="text-xs text-slate-500">Students with outstanding fees normally cannot start this exam. Waivers excuse a student (or all students) from that check.</p>
        </CardHeader>
        <CardContent className="space-y-4">
          <Card className={data?.blanket ? 'bg-emerald-50 border-emerald-200' : 'bg-slate-50'}>
            <CardContent className="p-3 flex items-center justify-between gap-3">
              <div>
                <p className="text-sm font-medium">Blanket waiver</p>
                <p className="text-xs text-slate-500">{data?.blanket ? `Active${data.blanket_reason ? ' — ' + data.blanket_reason : ''}. All students can start.` : 'Not active. Per-student waivers below still apply.'}</p>
              </div>
              {data?.blanket ? (
                <Button size="sm" variant="outline" onClick={() => revokeBlanket.mutate()} disabled={revokeBlanket.isPending} data-testid="revoke-blanket-waiver-btn">Revoke</Button>
              ) : (
                <div className="flex gap-2 items-center">
                  <Input placeholder="Reason (optional)" value={reason} onChange={e => setReason(e.target.value)} className="w-full max-w-[180px]" data-testid="blanket-waiver-reason" />
                  <Button size="sm" onClick={() => grantBlanket.mutate()} disabled={grantBlanket.isPending} data-testid="grant-blanket-waiver-btn">Grant blanket</Button>
                </div>
              )}
            </CardContent>
          </Card>

          <div>
            <Label className="text-xs">Grant per-student waiver</Label>
            <div className="flex gap-2 mt-1">
              <Input placeholder="Student ID (school_students _id)" value={studentId} onChange={e => setStudentId(e.target.value)} className="flex-1" data-testid="student-waiver-id" />
              <Input placeholder="Reason" value={reason} onChange={e => setReason(e.target.value)} className="w-full max-w-[180px]" data-testid="student-waiver-reason" />
              <Button size="sm" onClick={() => grantStudent.mutate()} disabled={grantStudent.isPending || !studentId} data-testid="grant-student-waiver-btn">Add</Button>
            </div>
          </div>

          <div>
            <p className="text-xs font-semibold text-slate-600 mb-1">Per-student waivers</p>
            {isLoading ? <Loader2 className="h-4 w-4 animate-spin text-slate-400" /> : !data?.students?.length ? (
              <p className="text-xs text-slate-400 italic">No per-student waivers yet.</p>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow><TableHead>Student</TableHead><TableHead>Reason</TableHead><TableHead>Granted</TableHead><TableHead className="w-[40px]"></TableHead></TableRow>
                </TableHeader>
                <TableBody>
                  {data.students.map(w => (
                    <TableRow key={w.id} data-testid={`waiver-row-${w.id}`}>
                      <TableCell className="text-sm">{w.student_name}<div className="text-[10px] text-slate-400">{w.admission_number || w.student_id}</div></TableCell>
                      <TableCell className="text-xs">{w.reason || '—'}</TableCell>
                      <TableCell className="text-[10px] text-slate-500">{w.created_at ? new Date(w.created_at).toLocaleString() : '—'}</TableCell>
                      <TableCell><Button size="icon" variant="ghost" className="h-7 w-7 text-red-400" onClick={() => revokeStudent.mutate(w.id)} data-testid={`revoke-waiver-${w.id}`}><UserMinus className="h-3.5 w-3.5" /></Button></TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

// ============================= MAIN COMPONENT =============================
export const SchoolExams = ({ tenantId }) => {
  const qc = useQueryClient();
  const [activeTab, setActiveTab] = useState('exams');
  const [selectedExam, setSelectedExam] = useState(null);
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState({ name: '', exam_type: 'term', class_ids: [], subject_ids: [], max_marks: 100, start_date: '', end_date: '', marking_mode: 'auto' });

  const { termId: acadTermId, term: acadTerm } = useAcademicContext(tenantId);
  const { data: exams = [], isLoading } = useQuery({
    queryKey: ['school-exams', tenantId, acadTermId],
    queryFn: () => api(`/tenants/${tenantId}/exams${acadTermId ? `?term_id=${acadTermId}` : ''}`),
  });
  const { data: classes = [] } = useQuery({ queryKey: ['school-classes', tenantId], queryFn: () => api(`/tenants/${tenantId}/classes`) });
  const { data: subjects = [] } = useQuery({ queryKey: ['school-subjects', tenantId], queryFn: () => api(`/tenants/${tenantId}/subjects`) });

  const createMutation = useMutation({
    mutationFn: (d) => api(`/tenants/${tenantId}/exams`, {
      method: 'POST',
      // Exams belong to the semester the admin is currently viewing.
      body: JSON.stringify({ ...d, term_id: d.term_id || acadTermId || null }),
    }),
    onSuccess: (r) => {
      const subjMsg = r.exam_subject_ids?.length ? ` · ${r.exam_subject_ids.length} subject(s) linked` : '';
      toast.success(`Exam "${r.name}" created${subjMsg}`);
      setShowCreate(false);
      setForm({ name: '', exam_type: 'term', class_ids: [], subject_ids: [], max_marks: 100, start_date: '', end_date: '', marking_mode: 'auto' });
      qc.invalidateQueries({ queryKey: ['school-exams'] });
    },
    onError: e => toast.error(e.message),
  });

  // EXAM DETAIL VIEW
  if (selectedExam) {
    return <ExamDetail tenantId={tenantId} exam={selectedExam} onBack={() => setSelectedExam(null)} classes={classes} subjects={subjects} />;
  }

  return (
    <div className="space-y-5" data-testid="exams-module">
      <CampusHolidayBanner tenantId={tenantId} />
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <div className="flex items-center justify-between">
          <TabsList>
            <TabsTrigger value="exams" data-testid="exams-tab"><ClipboardList className="h-4 w-4 mr-1" /> Exams</TabsTrigger>
            <TabsTrigger value="question-bank" data-testid="question-bank-tab-trigger"><BookOpen className="h-4 w-4 mr-1" /> Question Bank</TabsTrigger>
            <TabsTrigger value="auto-quiz" data-testid="auto-quiz-tab-trigger"><Sparkles className="h-4 w-4 mr-1" /> Auto Quiz</TabsTrigger>
            <TabsTrigger value="proctoring" data-testid="proctoring-tab-trigger"><ShieldCheck className="h-4 w-4 mr-1" /> Proctoring</TabsTrigger>
            <TabsTrigger value="exam-dashboard" data-testid="exam-dashboard-tab-trigger"><Trophy className="h-4 w-4 mr-1" /> Exam Dashboard</TabsTrigger>
            <TabsTrigger value="parent-digest" data-testid="parent-digest-tab-trigger"><GraduationCap className="h-4 w-4 mr-1" /> Parent Digest</TabsTrigger>
          </TabsList>
        </div>

        {/* EXAMS TAB */}
        <TabsContent value="exams" className="mt-4 space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-base font-semibold text-slate-900">Exams</h2>
              {acadTerm && (
                <p className="text-xs text-slate-500" data-testid="exams-semester-scope">
                  {acadTerm.name} — switch semester in the bar above to see another
                </p>
              )}
            </div>
            <Button size="sm" onClick={() => setShowCreate(true)} data-testid="create-exam-btn"><Plus className="h-4 w-4 mr-1" /> Create Exam</Button>
          </div>

          {isLoading ? <div className="flex justify-center py-12"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div> : exams.length === 0 ? (
            <Card className="border-dashed border-2">
              <CardContent className="py-12 text-center">
                <ClipboardList className="h-10 w-10 text-slate-300 mx-auto mb-3" />
                <p className="text-slate-500">No exams created yet</p>
                <Button variant="outline" className="mt-3" onClick={() => setShowCreate(true)}>Create First Exam</Button>
              </CardContent>
            </Card>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {exams.map(ex => {
                const si = EXAM_STATUS[ex.status] || EXAM_STATUS.draft;
                const mm = MARKING_MODES.find(m => m.value === ex.marking_mode);
                return (
                  <Card key={ex.id} className="cursor-pointer hover:shadow-md hover:border-indigo-200 transition-all" onClick={() => setSelectedExam(ex)} data-testid={`exam-card-${ex.id}`}>
                    <CardContent className="p-4">
                      <div className="flex items-start justify-between mb-2">
                        <h3 className="font-semibold text-slate-900 text-sm">{ex.name}</h3>
                        <Badge className={`text-[10px] ${si.color}`}>{si.label}</Badge>
                      </div>
                      <div className="flex items-center gap-2 mb-2">
                        <Badge variant="outline" className="capitalize text-[10px]">{ex.exam_type}</Badge>
                        {mm && <Badge variant="outline" className="text-[10px]"><mm.icon className="h-3 w-3 mr-0.5 inline" />{mm.label}</Badge>}
                        {ex.vetting_status && <Badge className={`text-[10px] ${VETTING_STATUS[ex.vetting_status]?.color || ''}`}>{ex.vetting_status}</Badge>}
                      </div>
                      <div className="flex items-center gap-4 text-[11px] text-slate-500">
                        <span><BookOpen className="h-3 w-3 inline mr-0.5" />{ex.subject_count} subjects</span>
                        <span><ListChecks className="h-3 w-3 inline mr-0.5" />{ex.question_count} questions</span>
                        <span><FileText className="h-3 w-3 inline mr-0.5" />{ex.marks_entered} marks</span>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}
        </TabsContent>

        {/* QUESTION BANK TAB */}
        <TabsContent value="question-bank" className="mt-4">
          <QuestionBankTab tenantId={tenantId} subjects={subjects} />
        </TabsContent>

        <TabsContent value="auto-quiz" className="mt-4">
          <AutoQuizTab tenantId={tenantId} subjects={subjects} classes={classes} />
        </TabsContent>
        <TabsContent value="proctoring" className="mt-4">
          <ProctoringTab tenantId={tenantId} exams={exams} />
        </TabsContent>
        <TabsContent value="exam-dashboard" className="mt-4">
          <ExamDashboardTab tenantId={tenantId} />
        </TabsContent>
        <TabsContent value="parent-digest" className="mt-4">
          <ParentDigestTab tenantId={tenantId} classes={classes} />
        </TabsContent>
      </Tabs>

      {/* Create Exam Dialog — iter-361 redesigned for 1-exam-per-subject.
          Subject is picked FIRST (single-select). Title auto-populates from
          the subject + type (editable). Backend independence guarantees
          unchanged — every action keys on (exam_id, subject_id, student_id). */}
      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Create Exam</DialogTitle>
            <p className="text-xs text-slate-500 mt-1">
              Each exam belongs to <strong>one subject</strong>. Activation,
              attempts, marks, and resets stay isolated from every other exam.
            </p>
          </DialogHeader>
          <div className="space-y-3">
            {/* SUBJECT — picked first so it can drive the auto-title */}
            <div data-testid="create-exam-subjects-section">
              <Label className="text-xs">Subject *</Label>
              {subjects.length === 0 ? (
                <p className="text-xs text-slate-400 mt-1">
                  No subjects defined in this school. Add a subject under
                  School → Subjects before creating an exam.
                </p>
              ) : (
                <Select
                  value={form.subject_ids[0] || ''}
                  onValueChange={(sid) => {
                    const subj = subjects.find(s => s.id === sid);
                    setForm(prev => {
                      // Auto-populate the title only when the user hasn't
                      // typed a custom one yet (preserve their intent).
                      const typeLabel = (EXAM_TYPES.find(t => t.value === prev.exam_type) || {}).label || '';
                      const suggested = subj ? `${subj.name} ${typeLabel}`.trim() : '';
                      const autoName = !prev.name || prev.name === prev._autoName ? suggested : prev.name;
                      return { ...prev, subject_ids: [sid], name: autoName, _autoName: suggested };
                    });
                  }}
                >
                  <SelectTrigger data-testid="exam-subject-select"><SelectValue placeholder="Pick the subject this exam covers…" /></SelectTrigger>
                  <SelectContent>
                    {subjects.map(s => (
                      <SelectItem key={s.id} value={s.id} data-testid={`exam-subject-toggle-${s.id}`}>
                        <span className="font-medium">{s.name}</span>
                        {s.code && <span className="text-slate-500 ml-2">· {s.code}</span>}
                        {s.department && <span className="text-slate-400 ml-2 text-[10px]">{s.department}</span>}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
              {form.subject_ids.length === 0 && subjects.length > 0 && (
                <p className="text-[11px] text-amber-700 mt-1" data-testid="create-exam-subject-warning">
                  Please select the subject for this exam.
                </p>
              )}
            </div>
            <div>
              <Label className="text-xs">Exam Name *</Label>
              <Input value={form.name}
                onChange={e => {
                  const newName = e.target.value;
                  setForm(prev => {
                    // iter-362 reverse subject match — if the user is typing and
                    // has NOT yet picked a subject, scan the existing subjects
                    // for a case-insensitive substring match on name or code
                    // and auto-select the first one found. Keyboard-only flow.
                    let nextSubjectIds = prev.subject_ids;
                    if (!prev.subject_ids.length && newName.length >= 3) {
                      const needle = newName.toLowerCase();
                      const match = subjects.find(s =>
                        (s.name && needle.includes(s.name.toLowerCase())) ||
                        (s.code && needle.includes(s.code.toLowerCase()))
                      );
                      if (match) nextSubjectIds = [match.id];
                    }
                    return { ...prev, name: newName, subject_ids: nextSubjectIds };
                  });
                }}
                placeholder="Pick a subject above to auto-fill"
                data-testid="exam-name-input" />
              {form.subject_ids.length === 1 && form.name && form._autoName !== form.name &&
               !(form._autoName || '').toLowerCase().includes(form.name.toLowerCase()) && (
                <p className="text-[10px] text-emerald-700 mt-0.5" data-testid="reverse-match-hint">
                  Subject auto-matched from name.
                </p>
              )}
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Type</Label>
                <Select value={form.exam_type} onValueChange={v => setForm(prev => {
                  // Refresh the suggested title when the type changes
                  const subj = subjects.find(s => s.id === (prev.subject_ids[0] || ''));
                  const typeLabel = (EXAM_TYPES.find(t => t.value === v) || {}).label || '';
                  const suggested = subj ? `${subj.name} ${typeLabel}`.trim() : prev.name;
                  const autoName = !prev.name || prev.name === prev._autoName ? suggested : prev.name;
                  return { ...prev, exam_type: v, name: autoName, _autoName: suggested };
                })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{EXAM_TYPES.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">Max Marks</Label>
                <Input type="number" value={form.max_marks} onChange={e => setForm({ ...form, max_marks: parseFloat(e.target.value) || 100 })} />
              </div>
            </div>
            {/* Marking Mode */}
            <div>
              <Label className="text-xs">Marking Mode *</Label>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 mt-1">
                {MARKING_MODES.map(m => (
                  <button key={m.value} onClick={() => setForm({ ...form, marking_mode: m.value })}
                    className={`p-3 rounded-lg border text-center transition-colors ${form.marking_mode === m.value ? 'bg-indigo-50 border-indigo-300 text-indigo-700' : 'bg-white border-slate-200 text-slate-500 hover:bg-slate-50'}`}
                    data-testid={`marking-mode-${m.value}`}
                  >
                    <m.icon className="h-5 w-5 mx-auto mb-1" />
                    <div className="text-xs font-medium">{m.label}</div>
                    <div className="text-[10px] opacity-60 mt-0.5">{m.desc}</div>
                  </button>
                ))}
              </div>
            </div>
            {/* Classes */}
            <div>
              <Label className="text-xs">Classes *</Label>
              <div className="flex flex-wrap gap-2 mt-1">
                {classes.map(c => (
                  <button key={c.id} onClick={() => setForm({ ...form, class_ids: form.class_ids.includes(c.id) ? form.class_ids.filter(id => id !== c.id) : [...form.class_ids, c.id] })}
                    className={`px-3 py-1 rounded-full text-xs font-medium border transition-colors ${form.class_ids.includes(c.id) ? 'bg-indigo-100 border-indigo-300 text-indigo-700' : 'bg-slate-50 border-slate-200 text-slate-500'}`}
                  >{c.name}</button>
                ))}
              </div>
            </div>
            {/* Subjects moved to the top of the dialog (single-select).
                The pre-iter-361 multi-chip block was removed because each
                exam is now strictly 1-subject. Existing multi-subject exams
                created before this iteration keep working unchanged. */}
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs">Start Date</Label><Input type="date" value={form.start_date} onChange={e => setForm({ ...form, start_date: e.target.value })} /></div>
              <div><Label className="text-xs">End Date</Label><Input type="date" value={form.end_date} onChange={e => setForm({ ...form, end_date: e.target.value })} /></div>
            </div>
          </div>
          <DialogFooter className="mt-3">
            <DialogClose asChild><Button variant="outline">Cancel</Button></DialogClose>
            <Button onClick={() => createMutation.mutate(form)} disabled={createMutation.isPending || !form.name || !form.class_ids.length || !form.subject_ids.length} data-testid="submit-exam-btn">
              {createMutation.isPending && <Loader2 className="h-4 w-4 animate-spin mr-1" />}
              Create Exam
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// ==================== PHASE B — AUTO QUIZ TAB ====================
// Phase B panels extracted to ExamsPhaseBPanels.jsx — see top-of-file import.


// ==================== Readiness Checklist wrapper ====================
// Thin shell that loads /workflow/exam/{tid}/{eid}/readiness and routes
// "Fix →" clicks to the right scroll target / tab inside ExamDetail. Adding
// new fix targets is just a new case in the switch below.
const ExamReadinessChecklist = ({ tenantId, examId }) => {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({
    queryKey: ['exam-readiness', tenantId, examId],
    queryFn: () => api(`/workflow/exam/${tenantId}/${examId}/readiness`),
    enabled: !!tenantId && !!examId,
    // Re-poll whenever the underlying exam detail invalidates so the
    // checklist stays in lock-step with whatever the admin is editing.
    refetchOnWindowFocus: true,
  });

  const publish = useMutation({
    mutationFn: () => api(`/workflow/exam/${tenantId}/${examId}/publish`, { method: 'POST' }),
    onSuccess: () => {
      toast.success('Exam activated — students can take it now.');
      qc.invalidateQueries({ queryKey: ['exam-readiness'] });
      qc.invalidateQueries({ queryKey: ['school-exam-detail'] });
      qc.invalidateQueries({ queryKey: ['school-exams'] });
    },
    onError: (e) => {
      // Backend returns a structured detail when blocked — surface it
      // verbatim instead of a vague toast.
      const det = e?.detail || e?.message;
      if (typeof det === 'object' && det?.blockers?.length) {
        toast.error(det.blockers[0]);
      } else {
        toast.error(typeof det === 'string' ? det : 'Cannot publish yet');
      }
    },
  });

  const handleFix = (fixKey) => {
    // Soft-scroll to the right region inside the detail page.
    const targets = {
      add_subjects: '[data-testid="add-exam-subject-btn"]',
      add_questions: '[data-testid="add-exam-subject-btn"]',
      assign_class: '[data-testid="exam-detail-panel"]',
      set_window: '[data-testid="exam-detail-panel"]',
      edit_instructions: '[data-testid="exam-detail-panel"]',
      edit_details: '[data-testid="exam-detail-panel"]',
    };
    const sel = targets[fixKey];
    if (sel) document.querySelector(sel)?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  };

  return (
    <WorkflowChecklist
      data={data}
      loading={isLoading}
      onAction={handleFix}
      onPublish={() => publish.mutate()}
      publishing={publish.isPending}
      publishLabel="Activate exam"
    />
  );
};

export default SchoolExams;
